import axios from 'axios';
import { ethers } from 'ethers';
import { encodeFunctionData } from 'viem';
import { TradeType } from '@sky-mavis/katana-swap';
import { Percent, CurrencyAmount, Token } from '@uniswap/sdk-core';
import { Pair, Route as V2Route, Trade as V2Trade } from '@uniswap/v2-sdk';
import { Pool, Route as V3Route, Trade as V3Trade } from '@uniswap/v3-sdk';

import useWrappedRPC from './useWrappedRPC';
import useEmbeddedWallet from './useEmbeddedWallet';
import environments from '../utils/environments';
import { abiERC20, abiStake, abiSwapRouter } from '../config/abi.contract';

const {
  ENVIRONMENT,
  CHAIN_ID,
  PUBLIC_RPC_URL,
  FISH_TOKEN_CONTRACT_ADDRESS,
  XFISH_TOKEN_CONTRACT_ADDRESS,
  STAKE_FISH_CONTRACT_ADDRESS,
  ROUTER_ADDRESS,
  WRON_ADDRESS,
} = environments;

const KATANA_ROUTING_API_URL = 'https://api.roninchain.com/routing-api/prod';
const DEFAULT_SWAP_SLIPPAGE = new Percent(50, 10_000); // 0.50%

export default function useToken() {
  const { WrappedJsonProvider, sendTransactionWithGas, customChainNetwork } = useWrappedRPC();
  const { getEmbeddedWallet } = useEmbeddedWallet();

  const provider = new WrappedJsonProvider(PUBLIC_RPC_URL, customChainNetwork);

  // ============================================================================
  // TOKEN BALANCE
  // ============================================================================

  const getFISHTokenBalance = async (address) => {
    try {
      if (!address) {
        const embeddedWallet = await getEmbeddedWallet();
        address = embeddedWallet.address;
      }
      const tokenContract = new ethers.Contract(FISH_TOKEN_CONTRACT_ADDRESS, abiERC20, provider);
      const res = await tokenContract.balanceOf(address);
      const balanceInWei = res.toString();
      return Number(ethers.utils.formatEther(balanceInWei));
    } catch (error) {
      console.error('Get FISH balance failed:', error);
      return 0;
    }
  };

  const getXFISHTokenBalance = async (address) => {
    try {
      if (!address) {
        const embeddedWallet = await getEmbeddedWallet();
        address = embeddedWallet.address;
      }
      const tokenContract = new ethers.Contract(XFISH_TOKEN_CONTRACT_ADDRESS, abiERC20, provider);
      const res = await tokenContract.balanceOf(address);
      const balanceInWei = res.toString();
      return Number(ethers.utils.formatEther(balanceInWei));
    } catch (error) {
      console.error('Get FISH balance failed:', error);
      return 0;
    }
  };

  // ============================================================================
  // TOKEN APPROVAL
  // ============================================================================

  const checkAndApproveToken = async ({ tokenAddress, ownerAddress, spenderAddress, amount }) => {
    const isNativeToken = tokenAddress === ethers.constants.AddressZero;
    if (isNativeToken) return;

    const parsedAmount = ethers.utils.parseEther(`${amount}`);
    const tokenContract = new ethers.Contract(tokenAddress, abiERC20, provider);
    const res = await tokenContract.allowance(ownerAddress, spenderAddress);
    const approvedAmountInWei = res.toString();
    const approvedAmountInToken = Number(ethers.utils.formatEther(approvedAmountInWei));
    const needApprovedMore = approvedAmountInToken < parsedAmount;

    if (needApprovedMore) {
      const approveValueBigint = ethers.constants.MaxUint256;
      const data = tokenContract.interface.encodeFunctionData('approve', [spenderAddress, approveValueBigint]);
      const txnRequest = { to: tokenAddress, data };
      const approveTxUiConfig = {
        header: 'Approve spending',
        description: `Allow Fishing Frenzy to spend ${amount} RON to purchase a diving ticket`,
        buttonText: 'Approve',
      };
      await sendTransactionWithGas(txnRequest, approveTxUiConfig);
    }
  };

  // #region STAKE
  const stakeFishToken = async (amount, durationInSeconds) => {
    try {
      const embeddedWallet = await getEmbeddedWallet();

      if (ENVIRONMENT === 'staging') {
        durationInSeconds = mapDurationForStaging(durationInSeconds);
      }

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiStake,
        functionName: 'stakeERC20',
        // eslint-disable-next-line no-undef
        args: [FISH_TOKEN_CONTRACT_ADDRESS, parsedAmount, BigInt(durationInSeconds)],
      });

      await checkAndApproveToken({
        tokenAddress: FISH_TOKEN_CONTRACT_ADDRESS,
        ownerAddress: embeddedWallet.address,
        spenderAddress: STAKE_FISH_CONTRACT_ADDRESS,
        amount,
      });

      const transactionRequest = {
        to: STAKE_FISH_CONTRACT_ADDRESS,
        data,
      };

      const durationInMonths = durationInSeconds / (30 * 24 * 3600);
      const gainedKarma = amount * durationInMonths;
      const formattedDuration = formatDuration(durationInSeconds);
      const txUiConfig = {
        header: `Stake`,
        description: `Stake ${amount} FISH for ${formattedDuration} to gain ${gainedKarma.toLocaleString()} Karma`,
        buttonText: `Confirm`,
      };

      const receipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!receipt) throw new Error('Transaction failed on blockchain');

      const updatedBalance = await getFISHTokenBalance(embeddedWallet.address);
      console.log('Updated FISH balance:', updatedBalance.toLocaleString());

      const formattedKarma = (Math.round(gainedKarma * 100) / 100).toLocaleString();

      return {
        success: true,
        message: `Stake successful:\n+${formattedKarma} Karma`,
        data: { gainedKarma, fishTokenBalance: updatedBalance },
      };
    } catch (error) {
      console.error('Stake FISH failed:', error);
      return { success: false, message: error.message };
    }
  };

  // #region SWAP

  const swapRonToFish = async (rawAmount) => {
    try {
      const embeddedWallet = await getEmbeddedWallet();

      const chainId = CHAIN_ID;
      const tokenInAddress = WRON_ADDRESS;
      const tokenOutAddress = FISH_TOKEN_CONTRACT_ADDRESS;
      const amount = ethers.utils.parseUnits(`${rawAmount}`, 18);
      const tradeType = TradeType.EXACT_INPUT;
      const deadline = Math.floor(Date.now() / 1000) + 60 * 20; // 20 minutes

      // Get quote from Katana API
      const quoteParams = {
        chainId,
        tokenInAddress,
        tokenOutAddress,
        amount,
        tradeType,
      };

      const response = await axios.get(
        `${KATANA_ROUTING_API_URL}/quote?${new URLSearchParams(
          Object.entries(getQuoteQueryParams(quoteParams))
        ).toString()}`
      );

      console.log('Quote response:', response.data);

      // Transform quote to trade object
      const trade = transformQuoteToTrade(quoteParams, response.data);
      console.log('Trade:', trade);

      // Calculate minimum amount out with slippage
      const slippageTolerance = new Percent(50, 10000); // 0.5%
      const minimumAmountOut = trade.minimumAmountOut(slippageTolerance);

      // Build execute calldata for Aggregate Router
      const { data: calldata } = buildExecuteData(
        amount,
        minimumAmountOut.quotient.toString(),
        trade,
        embeddedWallet.address,
        deadline
      );

      console.log('Swap details:', {
        amountIn: ethers.utils.formatEther(amount),
        amountOutMin: ethers.utils.formatEther(minimumAmountOut.quotient.toString()),
        trade,
        recipient: embeddedWallet.address,
        deadline,
      });

      // Execute swap (with RON value for wrapping)
      const txUiConfig = {
        header: 'Swap',
        description: `Swap ${rawAmount.toLocaleString()} RON to FISH`,
        buttonText: 'Swap',
      };

      const transactionRequest = {
        to: ROUTER_ADDRESS,
        data: calldata,
        value: amount, // Send RON to be wrapped & swapped
      };

      const txReceipt = await sendTransactionWithGas(transactionRequest, txUiConfig);

      const receipt = await provider.getTransactionReceipt(txReceipt.hash);
      console.log('Transaction receipt:', receipt);
      const swappedFishAmountInHex = getSwapTokenOutAmoutFromReceipt(receipt);
      const swappedFishAmount = parseFloat(ethers.utils.formatUnits(swappedFishAmountInHex, 18));
      console.log('swappedFishAmount', swappedFishAmount);

      const updatedBalance = await getFISHTokenBalance(embeddedWallet.address);
      console.log('Updated FISH balance:', updatedBalance.toLocaleString());

      return {
        success: true,
        message: `Spent ${rawAmount.toLocaleString()} RON and got\n${(
          Math.round(swappedFishAmount * 100) / 100
        ).toLocaleString()} FISH`,
        data: { transactionHash: txReceipt.hash, swappedFishAmount, fishTokenBalance: updatedBalance },
      };
    } catch (error) {
      console.error('Swap RON to FISH error:', error);

      return {
        success: false,
        message: error.message || 'Buy FISH failed',
      };
    }
  };

  /**
   * Build execute calldata for Aggregate Router
   * Supports both V2 and V3 swaps
   */
  function buildExecuteData(amountIn, amountOutMin, trade, recipient, deadline) {
    const abi = new ethers.utils.AbiCoder();

    // Detect if V3 trade
    const isV3 = Boolean(trade.swaps);

    // Commands: WRAP_ETH (0x0b) + V2_SWAP_EXACT_IN (0x08) or V3_SWAP_EXACT_IN (0x00)
    const swapCommand = isV3 ? '00' : '08';
    const commands = `0x0b${swapCommand}`;

    // Input 1: WRAP_ETH parameters
    // [address recipient, uint256 amountMin]
    // Use address(2) constant for "use contract's balance"
    const wrapInput = abi.encode(['address', 'uint256'], ['0x0000000000000000000000000000000000000002', amountIn]);

    let swapInput;

    if (isV3) {
      // V3_SWAP_EXACT_IN parameters
      // [address recipient, uint256 amountIn, uint256 amountOutMin, bytes path, bool payerIsUser]
      const path = getPathFromTrade(trade);
      const fees = trade.route.pools.map((pool) => pool.fee);

      console.log('path', path);
      // Encode V3 path: [token0, fee0, token1, fee1, token2, ...]
      let packedPath = path[0].address;
      for (let i = 0; i < fees.length; i++) {
        packedPath = ethers.utils.solidityPack(
          ['bytes', 'uint24', 'address'],
          [packedPath, fees[i], path[i + 1].address]
        );
      }

      swapInput = abi.encode(
        ['address', 'uint256', 'uint256', 'bytes', 'bool'],
        [recipient, amountIn, amountOutMin, packedPath, false]
      );
    } else {
      // V2_SWAP_EXACT_IN parameters
      // [address recipient, uint256 amountIn, uint256 amountOutMin, address[] path, bool payerIsUser]
      const path = getPathFromTrade(trade).map((token) => token.address);

      swapInput = abi.encode(
        ['address', 'uint256', 'uint256', 'address[]', 'bool'],
        [recipient, amountIn, amountOutMin, path, false]
      );
    }

    console.log('Encoded inputs:', {
      type: isV3 ? 'V3' : 'V2',
      wrapInput,
      swapInput,
      commands,
      deadline,
    });

    // Build execute calldata
    const data = encodeFunctionData({
      abi: abiSwapRouter,
      functionName: 'execute',
      args: [commands, [wrapInput, swapInput], deadline],
    });

    return { data, commands, inputs: [wrapInput, swapInput] };
  }

  // #region SWAP HELPERS
  const getSwapPreview = async ({ inputToken, amount }) => {
    try {
      let inputAmount, outputAmount, minimumOutput;
      if (inputToken === 'RON') {
        const res = await getPreviewSwapOutput({ amount });
        if (!res.success) throw new Error(res.error || 'Preview failed');
        inputAmount = amount;
        ({ outputAmount, minimumOutput } = res);
      } else {
        const maxInput = 100; // upper bound guess
        const res = await getPreviewSwapInput(amount, maxInput);
        if (!res) throw new Error('Preview failed');
        outputAmount = amount;
        minimumOutput = amount;
        ({ inputAmount } = res);
      }

      return {
        success: true,
        data: {
          inputToken: 'RON',
          outputToken: 'FISH',
          inputAmount,
          outputAmount,
          minimumOutput,
        },
      };
    } catch (error) {
      console.error('Get preview swap output failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to get quote',
      };
    }
  };

  /**
   * Get preview of swap output for user input
   * @param {Object} params
   * @param {string} params.inputToken - 'RON' or 'FISH'
   * @param {string|number} params.amount - Amount to swap
   * @returns {Promise<Object>} Preview data with output amount, price impact, etc.
   */
  const getPreviewSwapOutput = async ({ amount }) => {
    if (!amount || amount <= 0) {
      return null;
    }

    const chainId = CHAIN_ID;

    const tokenInAddress = WRON_ADDRESS;
    const tokenOutAddress = FISH_TOKEN_CONTRACT_ADDRESS;
    const parsedAmount = ethers.utils.parseUnits(`${amount}`, 18);
    const tradeType = TradeType.EXACT_INPUT;

    // Get quote from Katana API
    const quoteParams = {
      chainId,
      tokenInAddress,
      tokenOutAddress,
      amount: parsedAmount,
      tradeType,
    };

    const response = await axios.get(
      `${KATANA_ROUTING_API_URL}/quote?${new URLSearchParams(
        Object.entries(getQuoteQueryParams(quoteParams))
      ).toString()}`,
      { timeout: 5000 } // 5 second timeout
    );

    if (!response.data || !response.data.route) {
      throw new Error('No route found');
    }

    // Transform quote to trade object
    const trade = transformQuoteToTrade(quoteParams, response.data);
    console.log('Trade:', trade);

    // Calculate output with slippage
    const slippageTolerance = new Percent(50, 10000); // 0.5%
    const minimumAmountOut = trade.minimumAmountOut(slippageTolerance);

    // Format amounts
    const inputAmount = ethers.utils.formatEther(parsedAmount);
    const outputAmount = ethers.utils.formatEther(trade.outputAmount.quotient.toString());
    const minimumOutput = ethers.utils.formatEther(minimumAmountOut.quotient.toString());

    // Calculate price (how much output per 1 input)
    const price = parseFloat(outputAmount) / parseFloat(inputAmount);

    // Get price impact
    const priceImpact = trade.priceImpact.toSignificant(2);

    const path = getPathFromTrade(trade);

    return {
      success: true,
      inputToken: 'RON',
      outputToken: 'FISH',
      inputAmount: parseFloat(inputAmount),
      outputAmount: parseFloat(outputAmount),
      minimumOutput: parseFloat(minimumOutput),
      price, // e.g., 750.5 FISH per 1 RON
      priceImpact: parseFloat(priceImpact),
      slippage: 0.5, // 0.5%
      route: path.map((token) => token.symbol).join(' → '),
    };
  };

  /**
   * Optimized reverse swap calculation using Newton's method
   * Finds required input amount for desired output with minimal API calls
   */
  const getPreviewSwapInput = async (
    targetOut, // desired FISH amount
    maxInput = 100, // upper bound guess
    tolerance = 0.0001 // tolerance % in output amount (FISH) // 0.01% here
  ) => {
    const MAX_ITERATIONS = 8; // Limit iterations to prevent infinite loops
    let iteration = 0;

    // Initial guess: use a reasonable estimate based on typical swap rates
    let currentGuess = Math.min(targetOut / 700000, maxInput / 2);

    let lastOutput = 0;
    let lastInput = 0;
    const toleranceAmount = tolerance * targetOut;

    console.log(`🎯 Target: ${targetOut} FISH. ${toleranceAmount.toFixed(2)} tolerance (${tolerance * 100}%)`);

    while (iteration < MAX_ITERATIONS) {
      iteration++;

      // Get output for current guess
      const res = await getPreviewSwapOutput({ amount: currentGuess });
      if (!res.success) throw new Error('Preview failed');

      const { outputAmount, inputAmount } = res;
      const error = targetOut - outputAmount; // Positive = need more, Negative = got extra

      console.log(
        `📊 Iteration ${iteration}: ${currentGuess.toFixed(6)} RON → ${outputAmount.toFixed(
          2
        )} FISH (error: ${error.toFixed(2)})`
      );

      // CRITICAL: Only accept if we meet or exceed target AND within tolerance
      // error <= 0 means outputAmount >= targetOut (we got enough)
      // Math.abs(error) <= toleranceAmount means we're not too far over
      if (error <= 0 && Math.abs(error) <= toleranceAmount) {
        console.log(
          `✅ Converged in ${iteration} iterations (+${Math.abs(error).toFixed(2)} FISH extra, within tolerance)`
        );
        return {
          inputAmount: currentGuess,
          outputAmount,
          minimumOutput: res.minimumOutput,
        };
      }

      // Newton's method: use derivative (rate of change) to adjust guess
      if (iteration > 1 && lastInput !== currentGuess) {
        // Calculate approximate derivative: dOutput/dInput
        const derivative = (outputAmount - lastOutput) / (currentGuess - lastInput);

        if (derivative > 0) {
          // Newton step: newGuess = currentGuess + error/derivative
          const step = error / derivative;

          // Adaptive damping: more aggressive when undershooting, conservative when overshooting
          const dampingFactor = error > 0 ? 0.9 : 0.5;
          const nextGuess = currentGuess + step * dampingFactor;

          // Store current values for next iteration
          lastOutput = outputAmount;
          lastInput = currentGuess;

          // Clamp to reasonable bounds
          currentGuess = Math.max(0.001, Math.min(maxInput, nextGuess));
        } else {
          // Derivative is 0 or negative (shouldn't happen), fall back to binary search step
          currentGuess = error > 0 ? currentGuess * 1.1 : currentGuess * 0.95;
        }
      } else {
        // First iteration: adjust based on error magnitude
        lastOutput = outputAmount;
        lastInput = currentGuess;

        if (error > 0) {
          // Need more input (undershooting)
          const scaleFactor = 1 + Math.min(error / outputAmount, 0.5); // max 50% jump
          currentGuess *= scaleFactor;
        } else {
          // Got too much output (overshooting) - reduce more carefully
          const scaleFactor = 1 - Math.min(Math.abs(error) / outputAmount, 0.2); // max 20% reduction
          currentGuess *= scaleFactor;
        }

        currentGuess = Math.max(0.001, Math.min(maxInput, currentGuess));
      }
    }

    // If we exhausted iterations, ensure we meet the minimum requirement
    console.log(`⚠️ Max iterations reached. Ensuring minimum output...`);

    let finalGuess = currentGuess;
    const checkRes = await getPreviewSwapOutput({ amount: finalGuess });

    if (checkRes.outputAmount < targetOut) {
      // We're short, increase input with a safety margin
      const shortfall = targetOut - checkRes.outputAmount;
      const safetyFactor = 1.02; // 2% safety margin
      finalGuess = finalGuess * (1 + shortfall / checkRes.outputAmount) * safetyFactor;

      const finalRes = await getPreviewSwapOutput({ amount: finalGuess });
      console.log(`📈 Adjusted to ${finalGuess.toFixed(6)} RON → ${finalRes.outputAmount.toFixed(2)} FISH`);

      return {
        inputAmount: finalGuess,
        outputAmount: finalRes.outputAmount,
        minimumOutput: finalRes.minimumOutput,
      };
    }

    return {
      inputAmount: finalGuess,
      outputAmount: checkRes.outputAmount,
      minimumOutput: checkRes.minimumOutput,
    };
  };

  return {
    getFISHTokenBalance,
    getXFISHTokenBalance,
    checkAndApproveToken,
    stakeFishToken,
    swapRonToFish,
    getSwapPreview,
  };
}

// #region HELPER FUNCS

function formatDuration(seconds) {
  const units = [
    { label: 'month', secs: 30 * 24 * 3600 },
    { label: 'day', secs: 24 * 3600 },
    { label: 'hour', secs: 3600 },
    { label: 'minute', secs: 60 },
    { label: 'second', secs: 1 },
  ];

  for (let { label, secs } of units) {
    if (seconds >= secs) {
      const value = Math.floor(seconds / secs);
      return `${value} ${label}${value > 1 ? 's' : ''}`;
    }
  }
}

function mapDurationForStaging(durationInSeconds) {
  const MONTH_IN_SECONDS = 30 * 24 * 60 * 60;
  const mapping = {
    [3 * MONTH_IN_SECONDS]: 5 * 60,
    [6 * MONTH_IN_SECONDS]: 10 * 60,
    [12 * MONTH_IN_SECONDS]: 15 * 60,
  };
  return mapping[durationInSeconds] ?? durationInSeconds;
}

// ============================================================================
// QUOTE & TRADE HELPERS
// ============================================================================

function getQuoteQueryParams({ chainId, tradeType, amount, tokenInAddress, tokenOutAddress }) {
  return {
    tokenInChainId: chainId,
    tokenOutChainId: chainId,
    tokenInAddress,
    tokenOutAddress,
    amount: amount.toString(),
    type: tradeType === TradeType.EXACT_INPUT ? 'exactIn' : 'exactOut',
    intent: 'quote',
    enableUniversalRouter: true,
    enableFeeOnTransferFeeFetching: true,
    protocols: ['v2', 'v3', 'mixed'],
    slippageTolerance: DEFAULT_SWAP_SLIPPAGE.toSignificant(),
  };
}

/**
 * Custom Token class that bypasses Uniswap's chain validation
 */
class CustomToken extends Token {
  constructor(chainId, address, decimals, symbol) {
    // Use chainId 1 for parent validation, then override
    super(1, address, decimals, symbol);
    Object.defineProperty(this, 'chainId', {
      value: chainId,
      writable: false,
      enumerable: true,
      configurable: false,
    });
  }
}

/**
 * Transform Katana API quote response to Uniswap Trade object
 * Supports both V2 and V3 pools
 */
function transformQuoteToTrade(args, data) {
  const { chainId, tradeType, amount } = args;

  if (!data.route || !data.route[0] || data.route[0].length === 0) {
    throw new Error('No route found in quote data');
  }

  const routePath = data.route[0];
  const firstPool = routePath[0];
  const lastPool = routePath[routePath.length - 1];

  // Detect pool type
  const isV3 = firstPool.type === 'v3-pool';

  // Create tokens with custom class to bypass chain validation
  const tokenIn = new CustomToken(
    chainId,
    firstPool.tokenIn.address,
    parseInt(firstPool.tokenIn.decimals),
    firstPool.tokenIn.symbol
  );

  const tokenOut = new CustomToken(
    chainId,
    lastPool.tokenOut.address,
    parseInt(lastPool.tokenOut.decimals),
    lastPool.tokenOut.symbol
  );

  if (isV3) {
    // Handle V3 pools
    const pools = routePath.map((pool) => {
      const token0 = new CustomToken(
        chainId,
        pool.tokenIn.address,
        parseInt(pool.tokenIn.decimals),
        pool.tokenIn.symbol
      );

      const token1 = new CustomToken(
        chainId,
        pool.tokenOut.address,
        parseInt(pool.tokenOut.decimals),
        pool.tokenOut.symbol
      );

      return new Pool(
        token0,
        token1,
        parseInt(pool.fee), // Fee tier (e.g., 3000 = 0.3%)
        pool.sqrtRatioX96,
        pool.liquidity,
        parseInt(pool.tickCurrent)
      );
    });

    const route = new V3Route(pools, tokenIn, tokenOut);
    const trade = V3Trade.createUncheckedTrade({
      route,
      inputAmount: CurrencyAmount.fromRawAmount(tokenIn, amount.toString()),
      outputAmount: CurrencyAmount.fromRawAmount(tokenOut, lastPool.amountOut),
      tradeType,
    });

    return trade;
  } else {
    // Handle V2 pools
    const pairs = routePath.map((pool) => {
      const token0 = new CustomToken(
        chainId,
        pool.reserve0.token.address,
        parseInt(pool.reserve0.token.decimals),
        pool.reserve0.token.symbol
      );

      const token1 = new CustomToken(
        chainId,
        pool.reserve1.token.address,
        parseInt(pool.reserve1.token.decimals),
        pool.reserve1.token.symbol
      );

      return new Pair(
        CurrencyAmount.fromRawAmount(token0, pool.reserve0.quotient),
        CurrencyAmount.fromRawAmount(token1, pool.reserve1.quotient)
      );
    });

    const route = new V2Route(pairs, tokenIn, tokenOut);
    const trade = new V2Trade(route, CurrencyAmount.fromRawAmount(tokenIn, amount.toString()), tradeType);

    return trade;
  }
}

const getPathFromTrade = (trade) => {
  const v2PoolPath = trade.route?.path;
  const v3PoolPath = trade.swaps[0].route.tokenPath;

  return v2PoolPath || v3PoolPath;
};

// swap output is the value of the FISH Transfer event
const getSwapTokenOutAmoutFromReceipt = (receipt, isV3 = true) => {
  if (isV3) return receipt.logs[1].data; // v3 pool event
  else return receipt.logs[2].data; // v2 pool event
};
